import React, { useEffect, useMemo, useState } from "react";
import { ArrowIcon } from "../../../assets/images/svg";
import { useFilteredGoals } from "./hooks/useFilteredGoals";
import { useSortedGoals } from "./hooks/useSortedGoals";
import GoalListItem from "../goalListItemNew/goalListItem";
import MultiSelectDropdown from "../multiSelectDropdown/multiSelectDropdown";

const FilterDropdown = ({
  className,
  label,
  options,
  selected,
  placeholder,
  onChange,
  showFilter,
  ...restProps
}) => {
  return (
    <div className={className}>
      {showFilter && (
        <>
          <p>{label}</p>
          <MultiSelectDropdown
            options={options}
            selectedFilters={selected}
            placeholder={placeholder}
            includeSelectAll
            numberDisplayed={0}
            enableOnly
            haveOnSelectChange
            onSelectChange={onChange}
            {...restProps}
          />
        </>
      )}
    </div>
  );
};

const getValues = (data, valueName, maxDisplay = 10000) => {
  const values = [];
  data?.forEach((value, index) => {
    const title = value[valueName];
    if (index < maxDisplay && !values.includes(title)) {
      values.push(title);
    }
  });
  return values;
};

const GoalsList = ({
  isAnimated,
  goalData,
  zIndex,
  getGoalGraph,
  saveGoalAsValue,
  singleLocation,
  selectedView,
  goalsHeaderConfig,
  visibleColumns,
  goalsHeaderContent,
  moduleView,
  title,
  date,
  saveGoal
}) => {
  const { initialSelectedFilters, options } = goalsHeaderConfig.reduce(
    (acc, { key, dataKey, limit, type }) => {
      if (type !== "filter" || key === "display") return acc;

      const values = getValues(goalData, dataKey);
      acc.initialSelectedFilters[key] = limit ? values.slice(0, limit) : values;
      acc.options[key] = values;

      return acc;
    },
    {
      initialSelectedFilters: { display: ["ACTIVE", "INACTIVE"] },
      options: { display: ["ACTIVE", "INACTIVE"] }
    }
  );
  const [selectedFilters, setSelectedFilters] = useState(initialSelectedFilters);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "desc" });
  const filterableColumns = useMemo(
    () => goalsHeaderConfig.filter((elem) => elem.type === "filter"),
    [goalsHeaderConfig]
  );
  const filteredGoals = useFilteredGoals(goalData, filterableColumns, selectedFilters);
  const sortedGoals = useSortedGoals(filteredGoals, sortConfig);
  const [visibleGoals, setVisibleGoals] = useState(sortedGoals || []);

  useEffect(() => {
    const scrollY = window.scrollY;
    const html = document.documentElement;
    document.documentElement.style.scrollBehavior = "auto";
    setVisibleGoals(sortedGoals);

    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY });
      html.style.scrollBehavior = "smooth";
    });
  }, [sortedGoals]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "desc" };
      if (prev.direction === "desc") return { key, direction: "asc" };

      return { key: null, direction: null };
    });
  };

  const getSortArrow = (key) => {
    if (sortConfig.key === key)
      return sortConfig.direction === "asc" ? (
        <ArrowIcon className="sorting-icon up" />
      ) : (
        <ArrowIcon className="sorting-icon down" />
      );
  };

  const SortableColumn = ({ className, label, sortKey, condition = true }) => {
    if (!condition) return null;
    return (
      <p className={className}>
        <span className="sortable-column" onClick={() => handleSort(sortKey)}>
          {label}
          <span style={{ marginLeft: "3px" }}>{getSortArrow(sortKey)}</span>
        </span>
      </p>
    );
  };

  return (
    <div className="goals-list-wrapper" style={{ zIndex }}>
      <div className="goals-list-header">{goalsHeaderContent && goalsHeaderContent()}</div>
      {!goalData.length ? (
        <p className="error">Goals list is empty. Use Add Staff button to add a goal.</p>
      ) : (
        <>
          <div className="row-headers">
            {goalsHeaderConfig.map((col) => {
              const { key, type, visible, className, label, removeColumn } = col;
              if (removeColumn) return null;
              if (type === "filter") {
                return (
                  <FilterDropdown
                    key={title + label}
                    showFilter={visible}
                    className={className}
                    label={label}
                    options={options[key]}
                    selected={selectedFilters[key]}
                    onChange={(data) => setSelectedFilters((prev) => ({ ...prev, [key]: data }))}
                    {...col.additionalParam}
                  />
                );
              }
              if (type === "sortable") {
                return (
                  <SortableColumn
                    key={title + label}
                    className={className}
                    label={label}
                    sortKey={key}
                    condition={visible}
                  />
                );
              }
              return (
                visible && (
                  <p key={title + label} className={className}>
                    {label}
                  </p>
                )
              );
            })}
          </div>
          <div>
            {visibleGoals.map((value, idx) => {
              const { metricType, id } = value;
              return (
                <GoalListItem
                  isAnimated={isAnimated}
                  key={id ?? metricType + idx}
                  date={date}
                  moduleView={moduleView}
                  fetchGoalGraph={getGoalGraph}
                  singleLocation={singleLocation}
                  selectedView={selectedView}
                  visibleColumns={visibleColumns}
                  metricType={metricType}
                  saveGoal={saveGoal}
                  saveGoalAsValue={saveGoalAsValue}
                  goalData={value}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default GoalsList;
